import { isEmpty } from 'lodash';

const ELLIPSIS_DOTS_LENGTH = 3;

const ELLIPSIS_DOTS = '.'.repeat(ELLIPSIS_DOTS_LENGTH);

export class Utils {
  public static readonly ELLIPSIS_DOTS_LENGTH = ELLIPSIS_DOTS_LENGTH;

  public static readonly ELLIPSIS_DOTS = ELLIPSIS_DOTS;

  public static sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

  /**
   * Check whether received array-like value is empty
   *
   * @deprecated Use `isEmpty` function from `lodash` package instead
   *
   * @param value to check (any array-like value: array, string, etc.)
   *
   * @returns Check result (`boolean` value)
   */
  public static isEmpty = (value: ArrayLike<any>): boolean => isEmpty(value);

  /**
   * Check whether received value is `null` or `undefined`
   * but not the other `falsy` value
   *
   * @param value Value to check
   *
   * @returns Check result (`boolean` value)
   */
  public static isNil = (value?: any): value is null | undefined => {
    return value === null || value === undefined;
  };

  /**
   * Check whether received value is not `null` or `undefined` value
   * but can be the other `falsy` value
   *
   * @param value Value to check
   *
   * @returns Check result (`boolean` value)
   */
  public static isNotNil = (value?: any): boolean => !Utils.isNil(value);

  /**
   * Like `String.prototype.toUpperCase()` but only for the first letter.
   *
   * @param value
   */
  public static upperFirst = (value: string): string => {
    if (!value) {
      return '';
    }

    const [first, ...rest] = value.split('');
    return [first.toUpperCase(), ...rest].join('');
  };

  /**
   * Get first element of list
   *
   * @param [list] List (array of array-like value)
   *
   * @returns First element of `undefined` if list is empty or not defined
   */
  public static first = <T = any>(list?: ArrayLike<T>): T =>
    list ? list[0] : undefined;

  /**
   * Get last element of list
   *
   * @param list List (array of array-like value)
   *
   * @returns Last element of `undefined` if list is empty or not defined
   */
  public static last = <T = any>(list?: ArrayLike<T>): T =>
    list ? list[list.length - 1] : undefined;

  /**
   * Returns the total value of all elements of an array of numbers.
   * If received array empty or not defined then method returns zero.
   *
   * @param list Array of numbers to sum
   *
   * @returns Total value of all elements
   */
  public static sum = (list?: number[]): number => {
    if (Utils.isNil(list) || Utils.isEmpty(list)) {
      return 0;
    }

    return list.reduce((first, second) => first + second, 0);
  };

  /**
   * Split array-like value to chunks with fixed size (last chunk may be shorter).
   *
   * @param list List to split. Array-like value (array, string etc.)
   * @param size Size of chunk
   */
  public static chunk<T extends ArrayLike<T> = ArrayLike<string>>(list: T, size: number): T[] {
    if (size < 1 || list.length <= size) {
      return [list];
    }

    const chunksCount = Math.ceil(list.length / size);
    const result: T[] = [];
    for (let i = 0; i < chunksCount; i++) {
      result.push(
        typeof list === 'string'
          ? (<string>list).slice(i * size, i * size + size)
          : Array.prototype.slice.call(list, i * size, i * size + size),
      );
    }
    return result;
  }

  /** Formatted outpit of the float value */
  public static formatFloat = (value: number, precision?: number): string => {
    const [integer, fractional] = value.toFixed(precision ?? 2).split('.');

    const digits = integer.split('').reverse() as ArrayLike<string>;
    const chunks = Utils.chunk(digits, 3);

    const formatted = chunks
      .map(chunk => (<string[]>chunk).join(''))
      .join(' ')
      .split('')
      .reverse()
      .join('');

    return `${formatted}.${fractional}`;
  };

  public static getStackTrace = (): string[] => {
    const err = new Error();
    const stack = (err.stack || '').split('\n');
    return stack
      .slice(2)
      .map(chunk => chunk.slice(7));
  };

  public static substringAndEllipsis = (str: string, length = 200): string => {
    return str.length > length ? str.substring(0, length) + Utils.ELLIPSIS_DOTS : str;
  }

  /**
   * Converts markup to text
   * @param text
   * @param keepNewLines
   */
  public static convertHTMLToText = (text: string, keepNewLines = false): string => {
    const source = keepNewLines ? text.replace(/<br.?\/?>/gm, '\n') : text;
    return source.replace(/<blockquote.*>.*?<\/blockquote>/ig, '')
      .replace(/<\/p>/gi, '\n')
      .replace(/<li>/gi, '    ')
      .replace(/<\/li>/gi, '\n')
      .replace(/&nbsp;/gi, ' ')
      .replace(/(<([^>]+)>)/gi, '');
  };

  /**
   * Found percentage for value
   *
   * @param value Value to calculate
   * @param total Total value as percentage reference
   *
   * @returns Value in percents
   */
  public static percentOf = (value: number, total: number): number => total !== 0
    ? (value / total) * 100
    : 0;
}
